// FE-SCRM-053 — Contact detail page.
//
// Route ``/dashboard/sales-crm/contacts/:contactId``.
// Header + Overview with inline editing + Roles section (every
// OpportunityContactRole row for this contact across the entity's
// opportunities) + ActivityRail bound to ``contact_id``.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Badge, Table, TBody, THead, Th, Td, Tr,
} from "@/design-system";
import {
  DashboardErrorState, DashboardLoadingState,
} from "@/features/dashboard";

import { ActivityRail } from "../activities";

import {
  useContactDetailQuery,
  useContactRolesQuery,
  usePatchContactMutation,
} from "./hooks";
import type {
  ContactPatchInput, OpportunityContactRoleRow, SalesContact,
} from "./types";
import {
  DECISION_ROLE_LABEL, INFLUENCE_LEVEL_LABEL, STANCE_LABEL,
} from "./types";


export function ContactDetailPage(): JSX.Element {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();

  const detail_query = useContactDetailQuery(contactId);
  const roles_query = useContactRolesQuery(contactId);
  const patch_mutation = usePatchContactMutation();

  if (detail_query.isLoading) {
    return (
      <div data-testid="contact-detail-page">
        <DashboardLoadingState variant="card" />
      </div>
    );
  }
  if (detail_query.isError || !detail_query.data) {
    return (
      <div data-testid="contact-detail-page" className="flex flex-col gap-4">
        <DashboardErrorState
          title="Contact not found"
          description={
            "This contact may have been deleted or you don't have "
            + "access to its parent prospect."
          }
          retry={() => navigate("/dashboard/sales-crm?tab=contacts")}
        />
      </div>
    );
  }

  const contact = detail_query.data;
  const role_rows = roles_query.data?.rows ?? [];

  function patch(input: ContactPatchInput) {
    return patch_mutation.mutateAsync({
      contact_id: contact.contact_id, patch: input,
    });
  }

  return (
    <div
      data-testid="contact-detail-page"
      className="flex flex-col gap-6"
    >
      <ContactHeader contact={contact} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <ContactOverview
            contact={contact}
            on_patch={patch}
            is_submitting={patch_mutation.isPending}
          />
          <ContactRolesSection rows={role_rows} />
        </div>
        <aside>
          <ActivityRail contact_id={contact.contact_id} />
        </aside>
      </div>
    </div>
  );
}


// ── Header ─────────────────────────────────────────────────────────────


function ContactHeader({ contact }: { contact: SalesContact }) {
  return (
    <header data-testid="contact-detail-header" className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-text-primary">
          {contact.display_name}
        </h1>
        {contact.is_primary && <Badge variant="category">Primary</Badge>}
      </div>
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        {contact.title && <span>{contact.title}</span>}
        {contact.title && <span className="text-text-tertiary">·</span>}
        <Link
          to={`/dashboard/sales-crm/prospects/${contact.prospect_id}`}
          className="text-indigo-600 hover:underline"
        >
          {contact.prospect_name ?? "Open prospect"}
        </Link>
      </div>
      <Link
        to="/dashboard/sales-crm?tab=contacts"
        data-testid="contact-detail-back"
        className="mt-2 inline-flex w-fit text-xs text-text-tertiary hover:text-text-secondary"
      >
        ← Back to Contacts
      </Link>
    </header>
  );
}


// ── Overview with inline editing ───────────────────────────────────────


function ContactOverview({
  contact, on_patch, is_submitting,
}: {
  contact: SalesContact;
  on_patch: (input: ContactPatchInput) => Promise<SalesContact>;
  is_submitting: boolean;
}) {
  return (
    <section
      data-testid="contact-detail-overview"
      className={
        "rounded-md border border-gray-200 bg-white p-4 "
        + "flex flex-col gap-3"
      }
    >
      <h2 className="text-sm font-semibold text-text-primary">
        Overview
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InlineTextField
          testid="contact-field-first-name"
          label="First name"
          value={contact.first_name}
          disabled={is_submitting}
          onSubmit={(v) => on_patch({ first_name: v })}
        />
        <InlineTextField
          testid="contact-field-last-name"
          label="Last name"
          value={contact.last_name}
          disabled={is_submitting}
          onSubmit={(v) => on_patch({ last_name: v })}
        />
        <InlineTextField
          testid="contact-field-title"
          label="Title"
          value={contact.title ?? ""}
          disabled={is_submitting}
          onSubmit={(v) => on_patch({ title: v || null })}
          allow_blank
        />
        <InlineTextField
          testid="contact-field-email"
          label="Email"
          value={contact.email ?? ""}
          disabled={is_submitting}
          onSubmit={(v) => on_patch({ email: v || null })}
          allow_blank
        />
        <InlineTextField
          testid="contact-field-phone"
          label="Phone"
          value={contact.phone ?? ""}
          disabled={is_submitting}
          onSubmit={(v) => on_patch({ phone: v || null })}
          allow_blank
        />
        <PrimaryToggle
          contact={contact}
          on_patch={on_patch}
          is_submitting={is_submitting}
        />
      </div>
      <InlineTextarea
        testid="contact-field-notes"
        label="Notes"
        value={contact.notes ?? ""}
        disabled={is_submitting}
        onSubmit={(v) => on_patch({ notes: v || null })}
      />
    </section>
  );
}


function InlineTextField({
  testid, label, value, disabled, onSubmit, allow_blank,
}: {
  testid: string;
  label: string;
  value: string;
  disabled: boolean;
  onSubmit: (next: string) => Promise<unknown>;
  allow_blank?: boolean;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);

  function commit() {
    const trimmed = local.trim();
    if (trimmed === value) return;
    if (!trimmed && !allow_blank) {
      setLocal(value); // revert: required field can't be cleared
      return;
    }
    onSubmit(trimmed).catch(() => setLocal(value));
  }

  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <input
        type="text"
        data-testid={testid}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        disabled={disabled}
        className={
          "rounded-md border border-gray-300 bg-white px-2 py-1 "
          + "text-sm focus:border-indigo-500 focus:outline-none "
          + "focus:ring-1 focus:ring-indigo-500"
        }
      />
    </label>
  );
}


function InlineTextarea({
  testid, label, value, disabled, onSubmit,
}: {
  testid: string;
  label: string;
  value: string;
  disabled: boolean;
  onSubmit: (next: string) => Promise<unknown>;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);

  function commit() {
    const trimmed = local.trim();
    if (trimmed === value) return;
    onSubmit(trimmed).catch(() => setLocal(value));
  }

  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <textarea
        data-testid={testid}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        disabled={disabled}
        rows={3}
        className={
          "rounded-md border border-gray-300 bg-white px-2 py-1 "
          + "text-sm focus:border-indigo-500 focus:outline-none "
          + "focus:ring-1 focus:ring-indigo-500"
        }
      />
    </label>
  );
}


function PrimaryToggle({
  contact, on_patch, is_submitting,
}: {
  contact: SalesContact;
  on_patch: (input: ContactPatchInput) => Promise<SalesContact>;
  is_submitting: boolean;
}) {
  return (
    <label className="flex items-center gap-2 self-end pb-1">
      <input
        type="checkbox"
        data-testid="contact-field-primary"
        checked={contact.is_primary}
        disabled={is_submitting}
        onChange={(e) => on_patch({ is_primary: e.target.checked })}
        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span className="text-sm text-text-secondary">
        Primary contact at this Prospect
      </span>
    </label>
  );
}


// ── Roles section ──────────────────────────────────────────────────────


function ContactRolesSection({ rows }: { rows: OpportunityContactRoleRow[] }) {
  return (
    <section
      data-testid="contact-detail-roles"
      className={
        "rounded-md border border-gray-200 bg-white p-4 "
        + "flex flex-col gap-3"
      }
    >
      <h2 className="text-sm font-semibold text-text-primary">
        Stakeholder roles
      </h2>
      {rows.length === 0 ? (
        <p
          data-testid="contact-detail-roles-empty"
          className="text-xs text-text-tertiary"
        >
          This contact isn't on any Opportunity yet. Add them from the
          Opportunity's Stakeholder map.
        </p>
      ) : (
        <Table dividers>
          <THead className="border-b border-border-subtle bg-recessed text-xs uppercase tracking-wide text-text-secondary">
            <Tr>
              <Th>Opportunity</Th>
              <Th>Role</Th>
              <Th>Influence</Th>
              <Th>Stance</Th>
              <Th>Last touchpoint</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((r) => (
              <Tr
                key={r.role_id}
                data-testid={`contact-detail-role-${r.role_id}`}
              >
                <Td className="font-medium">
                  <Link
                    to={`/dashboard/sales-crm/opportunities/${r.opportunity_id}`}
                    className="text-teal hover:underline"
                  >
                    {r.opportunity_name}
                  </Link>
                </Td>
                <Td className="text-text-secondary">
                  {DECISION_ROLE_LABEL[r.decision_role]}
                </Td>
                <Td className="text-text-secondary">
                  {INFLUENCE_LEVEL_LABEL[r.influence_level]}
                </Td>
                <Td className="text-text-secondary">
                  {STANCE_LABEL[r.stance]}
                </Td>
                <Td className="text-text-tertiary text-xs">
                  {r.last_touchpoint_at
                    ? r.last_touchpoint_at.slice(0, 10)
                    : "Never"}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}
